import { HttpErrorResponse } from "@angular/common/http";

import { FiscalRegime } from "@core/models/fiscal-regime.model";
import { FundWithRelated } from "@core/models/fund-with-related.model";
import { Product } from "@core/models/product.model";
import { ProductKind } from "@core/models/product-kind.model";
import { ProductsInfo } from "@core/models/products-info.model";
import { TPagination } from "@custom-types/sorting";
import { createReducer, on } from "@ngrx/store";

import * as ProductActions from "../store/product.actions";

export const PRODUCT_FEATURE_KEY = "product";

export interface ProductState {
  products: Array<Product>;
  productsInfo: ProductsInfo;
  loading: boolean;
  error: HttpErrorResponse | null;
  funds: Array<FundWithRelated>;
  fiscalRegime: Array<FiscalRegime>;
  productKind: Array<ProductKind>;
  nonDeviatedCostsCount: number;
  pagination: Partial<TPagination> | undefined;
}

const initialState: ProductState = {
  products: [],
  productsInfo: new ProductsInfo(),
  loading: false,
  error: null,
  funds: [],
  fiscalRegime: [],
  productKind: [],
  nonDeviatedCostsCount: -1,
  pagination: undefined,
};

export const PRODUCT_REDUCER = createReducer(
  initialState,
  on(
    ProductActions.SET_PRODUCTS,
    (
      state: ProductState,
      action: { productList: Array<Product> }
    ): ProductState => {
      const filter = [...action.productList];
      const productsWithSortedVersionsList = filter.map(
        (product: Product): Product => {
          const productVersion = [...product.versions];
          return {
            ...product,
            versions: productVersion.sort(
              (a, b) => a.gfhVersionNumber - b.gfhVersionNumber
            ),
          };
        }
      );
      return {
        ...state,
        products: productsWithSortedVersionsList,
      };
    }
  ),
  on(
    ProductActions.SET_PRODUCTS_LOADING_STATUS,
    (state: ProductState, action: { loading: boolean }): ProductState => {
      return {
        ...state,
        loading: action.loading,
      };
    }
  ),
  on(
    ProductActions.SET_PRODUCTS_ERROR,
    (
      state: ProductState,
      action: { error: HttpErrorResponse }
    ): ProductState => {
      return {
        ...state,
        error: action.error,
      };
    }
  ),
  on(
    ProductActions.SET_FUNDS,
    (
      state: ProductState,
      action: { fundsList: Array<FundWithRelated> }
    ): ProductState => {
      return {
        ...state,
        funds: action.fundsList,
      };
    }
  ),
  on(
    ProductActions.SET_FISCAL_REGIME,
    (
      state: ProductState,
      action: {
        fiscalRegimeList: Array<FiscalRegime>;
      }
    ): ProductState => {
      return {
        ...state,
        fiscalRegime: action.fiscalRegimeList,
      };
    }
  ),
  on(
    ProductActions.SET_PRODUCTS_INFO,
    (
      state: ProductState,
      action: { productsInfo: ProductsInfo }
    ): ProductState => {
      return {
        ...state,
        productsInfo: action.productsInfo,
      };
    }
  ),
  on(
    ProductActions.SET_PRODUCT_KIND,
    (
      state: ProductState,
      action: {
        productKindList: Array<ProductKind>;
      }
    ): ProductState => {
      return {
        ...state,
        productKind: action.productKindList,
      };
    }
  ),
  on(
    ProductActions.SET_NON_DEVIATED_COSTS_COUNT,
    (
      state: ProductState,
      action: {
        nonDeviatedCostsCount: number;
      }
    ): ProductState => {
      return {
        ...state,
        nonDeviatedCostsCount: action.nonDeviatedCostsCount,
      };
    }
  ),
  on(
    ProductActions.SET_PRODUCTS_PAGINATION,
    (
      state: ProductState,
      action: {
        pagination: Partial<TPagination> | undefined;
      }
    ): ProductState => {
      return {
        ...state,
        pagination: action.pagination,
      };
    }
  )
);
